/**
 * Financial stats (`payments/stats/*`, permission `finance_stats_read`).
 * Query: `from`/`to` (ISO; defaults to the last 30 days), `tenantId`
 * (`'all'` = cross-tenant, platform user only). A non-platform user is
 * automatically scoped to their organizations on the BE.
 *
 * BE returns RAW responses (no `{ data }` wrapper). Monetary amounts and
 * COUNT aggregates are STRING (BIGINT) — displayed via `utils/currency.ts`.
 */
import type {
  ApiStatsPlatformRevenue,
  ApiStatsEarningsByTrainer,
  FetchStatsBreakdownArgs,
  ApiStatsReconciliation,
  FinanceStatsRangeArgs,
  ApiStatsSubscriptions,
  ApiStatsMarketplace,
  ApiStatsBreakdown,
  ApiStatsExpenses,
  ApiStatsOverview,
} from './types/payments.types';

import { get } from './axios';

/**
 * The wire form of the shared range. `currency` is NOT in it on purpose: every
 * endpoint except `breakdown` returns per-currency rows that the section
 * filters on the client, so sending it would only fragment the cache into
 * identical responses. `fetchStatsBreakdown` adds it back by hand.
 */
function rangeQuery({
  organizationId,
  tenantId,
  from,
  to,
}: FinanceStatsRangeArgs) {
  return { from, organizationId, tenantId, to };
}

// The viewer's IANA zone used to travel here as `tz`. Foundation 1 (D6/D7)
// moves the cut to the ORGANIZATION's own zone, decided server-side from the
// organization itself — the viewer's browser zone is no longer relevant to
// these reports, so nothing is sent and nothing is cached on it.

export const fetchStatsOverview = async (args: FinanceStatsRangeArgs) => {
  const response = await get<ApiStatsOverview>('/v2/payments/stats/overview', {
    query: rangeQuery(args),
  });
  return response ?? null;
};

/**
 * Organization COST side: what the org paid the platform (subscription +
 * one-off purchases such as the lifetime rebrand). Deliberately NOT part of the
 * revenue endpoints — money leaving the org is never its income.
 */
export const fetchStatsExpenses = async (args: FinanceStatsRangeArgs) => {
  const response = await get<ApiStatsExpenses>('/v2/payments/stats/expenses', {
    query: rangeQuery(args),
  });
  return response ?? null;
};

/**
 * The platform's income from organizations, ranked per organization.
 * Platform user only (otherwise 403).
 */
export const fetchStatsPlatformRevenue = async (
  args: FinanceStatsRangeArgs,
) => {
  const response = await get<ApiStatsPlatformRevenue>(
    '/v2/payments/stats/platform-revenue',
    { query: rangeQuery(args) },
  );
  return response ?? null;
};

export const fetchStatsSubscriptions = async (args: FinanceStatsRangeArgs) => {
  const response = await get<ApiStatsSubscriptions>(
    '/v2/payments/stats/subscriptions',
    { query: rangeQuery(args) },
  );
  return response ?? null;
};

export const fetchStatsBreakdown = async ({
  dimension,
  limit,
  offset,
  rank,
  ...range
}: FetchStatsBreakdownArgs) => {
  const response = await get<ApiStatsBreakdown>(
    '/v2/payments/stats/breakdown',
    {
      query: {
        ...rangeQuery(range),
        // explicitly, because `rangeQuery` deliberately drops it: this is the
        // ONE endpoint that filters by currency server-side (it is paginated,
        // and `total` counts (value, currency) groups)
        currency: range.currency,
        dimension,
        limit: limit?.toString(),
        offset: offset?.toString(),
        rank,
      },
    },
  );
  return response ?? null;
};

export const fetchStatsMarketplace = async (args: FinanceStatsRangeArgs) => {
  const response = await get<ApiStatsMarketplace>(
    '/v2/payments/stats/marketplace',
    { query: rangeQuery(args) },
  );
  return response ?? null;
};

/** Platform user only (otherwise 403). */
export const fetchStatsReconciliation = async (args: FinanceStatsRangeArgs) => {
  const response = await get<ApiStatsReconciliation>(
    '/v2/payments/stats/reconciliation',
    { query: rangeQuery(args) },
  );
  return response ?? null;
};

/** Task B3: per (client, currency) earnings attributed to a trainer. */
export const fetchStatsEarningsByTrainer = async (
  args: FinanceStatsRangeArgs,
) => {
  const response = await get<ApiStatsEarningsByTrainer>(
    '/v2/payments/stats/earnings-by-trainer',
    { query: rangeQuery(args) },
  );
  return response ?? null;
};
